import { INTERNAL_EMAIL_ADDRESSES as SUPPRESSED_EMAILS } from '@overlayqa/shared';
/**
 * Dashboard PostHog analytics.
 *
 * Uses the same project (375980) as the extension and marketing site.
 * Key is loaded from VITE_POSTHOG_KEY env var.
 */
import posthog from 'posthog-js';
import { isExtensionSessionRoute } from './extensionSessionRoute';
// Ship the recorder with the app so a failed lazy script request cannot leave
// an otherwise eligible session stuck in lazy_loading. The SDK still owns its
// remote recording configuration, masking and opt-out checks before starting.
import 'posthog-js/dist/posthog-recorder';
import { isReportableBranch, resolveInitBranch, safePathLabel } from './analyticsInitBranch';
import { resolveInstallLanding } from './installLanding';
import { POSTHOG_HOST, buildPostHogConfig } from './posthogConfig';
import { createNetworkFailureReporter } from './api/networkDiagnostics';
import { NETWORK_FAILURE_EVENT, type NetworkFailureProps } from './api/networkFailure';
import {
  isEmailScannerUserAgent,
  markSessionIdentified,
  readViewportFingerprint,
  setScannerLatchListener,
  viewportFingerprintInput,
} from './scannerNoise';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';


let initialized = false;
let suppressedInternal = false;

const networkFailureReporter = createNetworkFailureReporter({
  host: POSTHOG_HOST,
  key: POSTHOG_KEY,
  capture: (event, properties) => posthog.capture(event, properties),
  canCapture: () => initialized && !suppressedInternal && !posthog.has_opted_out_capturing(),
});

/**
 * Send an opt-in diagnostic beacon by raw `fetch`.
 * Normal builds keep these startup details out of the product event stream.
 * An investigation build can set VITE_POSTHOG_INIT_DIAGNOSTICS=true; the flag
 * is compiled in, so a URL or browser-storage value cannot enable reporting.
 * Failure, recovery and scanner-latch events do not use this switch.
 *
 * NEVER `posthog.capture`: every mechanism that could explain a dark page load
 * sits downstream of it (the `initialized` gate, the `before_send` filter chain,
 * posthog-js's own transport), so a report travelling by the suspect channel
 * could be swallowed by the very fault it exists to name.
 *
 * The two paths are also physically different, which is what makes a beacon
 * DISCRIMINATING rather than merely redundant: posthog-js posts events to
 * `/oqi/e/`, this posts to `/oqi/capture/`. A beacon landing while `$pageview`
 * never does localises the fault to posthog-js's own pipeline instead of to the
 * network -- OR to posthog-js dropping the browser on purpose, which is why every
 * beacon carries `likely_bot` (see readLikelyBot). Verified live 2026-09-13:
 * `POST /oqi/capture/` is answered by PostHog's ingestion endpoint
 * (`{"status":"Ok"}`, application/json), not by Vercel's SPA fallback -- the
 * `/oqi/:path(.*)` rewrite in vercel.json covers it.
 */
function sendInitDiagnostic(
  event: string,
  distinctId: string,
  properties: Record<string, unknown>,
): void {
  if (import.meta.env.VITE_POSTHOG_INIT_DIAGNOSTICS !== 'true') return;

  void fetch(`${POSTHOG_HOST}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ api_key: POSTHOG_KEY, distinct_id: distinctId, event, properties }),
  }).catch(() => {
    // Analytics must never break the page.
  });
}

/**
 * posthog-js's own verdict on whether this browser is an automated one.
 *
 * It is the FIRST gate `capture()` checks -- before consent, before the rate
 * limiter, before `before_send` -- and the only one that returns with no log
 * line even in debug mode, so `is_capturing()` (consent only) reads true while
 * every capture is dropped. A raw-fetch beacon bypasses that gate along with
 * everything else, which is the blind spot this closes: measured 2026-09-14,
 * all 24 `posthog_init_completed` beacons after the 2026-09-13 deploy, every
 * one `capturing: true`, were this network's own harness fleet in headless
 * Chromium (`HeadlessChrome` in `navigator.userAgentData.brands`,
 * `navigator.webdriver` true), and read as every dashboard user gone dark while
 * a human-shaped browser on the same build captured normally.
 *
 * Read off the instance rather than re-derived: the blocked-token list and the
 * brand check live in posthog-js (`isLikelyBot`, @posthog/core `isBlockedUA`)
 * and a local copy would drift. `_is_bot()` is declared public in
 * posthog-core.d.ts and is readable before `init()`, because `config` is
 * defaulted in the constructor. Guarded because the library's health is one of
 * the things these beacons investigate.
 */
function readLikelyBot(): boolean | null {
  try {
    return Boolean(posthog._is_bot());
  } catch {
    return null;
  }
}

export function initPostHog(): void {
  // Automatic extension session checks are background work, not page visits.
  // Their bounded outcome is reported by the extension, including failures.
  if (isExtensionSessionRoute(window.location.pathname)) return;
  // Name the branch this call takes, BEFORE any of the returns below.
  //
  // All four early returns produce the same observable result -- total browser
  // silence for the page load, because `capture()` is gated on the `initialized`
  // flag they skip -- so a guard that fired and a page that never loaded are
  // indistinguishable in PostHog. That is the hole the 2026-09-13 read could not
  // close: 14 of 132 accounts that finished the onboarding wizard emit no
  // `$lib='web'` event on app.overlayqa.com, ever, while the same 14 emit
  // extension, server-side and api-origin web events normally.
  //
  // resolveInitBranch MODELS these guards rather than replacing them. Letting it
  // drive the returns would mean a bug in it takes every user's analytics dark;
  // used only for the label, the worst it can do is mislabel a beacon. A
  // diagnostic must not be able to break the thing it observes. The price is two
  // copies of the conditions, pinned together by the CONTROL in
  // __tests__/analyticsInitBeacon.test.ts.
  const branch = resolveInitBranch({
    hasKey: Boolean(POSTHOG_KEY),
    alreadyInitialized: initialized,
    hostname: window.location.hostname,
    allowLocalhost: import.meta.env.VITE_ALLOW_LOCALHOST_ANALYTICS === 'true',
    isScannerUa: isEmailScannerUserAgent(window.navigator.userAgent),
  });
  if (isReportableBranch(branch)) {
    sendInitDiagnostic('posthog_init_branch', `init-branch:${window.location.hostname}`, {
      branch,
      likely_bot: readLikelyBot(),
      route: safePathLabel(window.location.pathname),
    });
  }

  if (initialized || !POSTHOG_KEY) return;

  // Never send analytics from local dev servers.
  //
  // The one exception is a BUILD explicitly compiled to allow it, which exists
  // so the analytics pipeline can be driven in a real browser at all. The two
  // constraints are otherwise irreconcilable: PostHog only initialises off
  // localhost, and Clerk's dev instance only works ON localhost — measured
  // 2026-09-10 against a loopback alias (lvh.me), where Clerk answered 400,
  // `auth_load_failed` fired, and the Clerk auto-retry reloaded the page,
  // clearing the module state the harness was measuring.
  //
  // `import.meta.env` is frozen into the bundle at build time, so this cannot be
  // flipped by a page, a query string or localStorage: a real localhost session
  // could opt itself in, and then a developer's clicking would land in project
  // 375980. Vercel's production build does not set it.
  const allowLocalhost = import.meta.env.VITE_ALLOW_LOCALHOST_ANALYTICS === 'true';
  if (!allowLocalhost && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return;
  }

  // Corporate email-security scanners detonate every link in our lifecycle mail
  // within seconds of delivery. Left alone they mint a full anonymous session
  // (sign_in_page_viewed, auth_load_failed, $exception rows), which reads as a
  // real user hitting a broken sign-in. Never initialise for one — the same
  // no-analytics-at-all posture the localhost guard above takes, and the same
  // one lib/installLanding.ts already applies to automated browsers.
  // Scanners on a plain browser UA are caught later by the session latch in
  // lib/scannerNoise.ts instead.
  if (isEmailScannerUserAgent(window.navigator.userAgent)) {
    return;
  }

  // Count the silences. Once the CefSharp bridge signature fires,
  // dropWhenScannerSession drops the rest of the page load, so a silenced
  // session is otherwise indistinguishable from a session that never happened —
  // which is exactly the hole the 2026-09-10 read could not close (4 of 9
  // signups that week emitted no web events at all). One beacon per page load.
  //
  // Sent by raw fetch, the captureInstallLanded pattern, NOT posthog.capture:
  // the filter that dropped everything would drop the report of the drop too.
  // `identified` is the field worth alerting on — after markSessionIdentified()
  // below it should be ~never true, and a true means a real signed-in user's
  // browser threw the third-party signature.
  setScannerLatchListener(({ identified }) => {
    void fetch(`${POSTHOG_HOST}/capture/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: POSTHOG_KEY,
        distinct_id: `scanner-latch:${window.location.hostname}`,
        event: 'scanner_session_latched',
        properties: { identified, route: safePathLabel(window.location.pathname) },
      }),
    }).catch(() => {
      // Analytics must never break the page.
    });
  });

  posthog.init(POSTHOG_KEY, buildPostHogConfig());

  // The email-security sandbox fingerprint rides every event of this page load
  // as super properties, posthog-js's own $exception captures included, so a
  // read excludes the class with one predicate instead of re-deriving viewport,
  // screen, timezone and referrer each time (measured 2026-10-05: 32 of 41
  // people on auth_load_failed). Tagged, never dropped: see scannerNoise.ts.
  const viewportFingerprint = readViewportFingerprint(viewportFingerprintInput(window));
  try {
    posthog.register(viewportFingerprint);
  } catch {
    // Analytics must never break the page.
  }

  initialized = true;

  // Init returned without throwing and the library is live. Deliberately AFTER
  // `initialized = true`, so the diagnostic can never be what stops the product
  // working.
  //
  // Keyed to posthog-js's OWN distinct id, the captureInstallLanded trick: the
  // beacon then folds into the person when identify() runs, so a dark signup is
  // NAMEABLE in Postgres rather than merely countable. Read defensively because
  // the library being broken is one of the things under investigation.
  let distinctId: string | null = null;
  let capturing: boolean | null = null;
  try {
    distinctId = posthog.get_distinct_id() || null;
    capturing = posthog.is_capturing();
  } catch {
    // Analytics must never break the page.
  }
  sendInitDiagnostic(
    'posthog_init_completed',
    distinctId ?? `init-completed:${window.location.hostname}`,
    {
      capturing,
      likely_bot: readLikelyBot(),
      likely_email_sandbox: viewportFingerprint.likely_email_sandbox,
      folded: distinctId !== null,
      route: safePathLabel(window.location.pathname),
    },
  );
}

/**
 * Identify the signed-in user. Suppresses internal accounts by opting
 * them out of posthog-js entirely (blocks autocapture, pageview, etc.).
 * Fires a manual $pageview for real users on first identify.
 */
export function identify(userId: string, email: string, name?: string): void {
  if (!POSTHOG_KEY || !initialized) return;

  const emailLower = email.toLowerCase();
  if (SUPPRESSED_EMAILS.has(emailLower) || emailLower.endsWith('@overlayqa.com')) {
    // Opt out at the posthog-js level so built-in events (pageview,
    // pageleave, autocapture) are also suppressed, not just our wrapper.
    posthog.opt_out_capturing();
    // opt_out_capturing suppresses transmission, but the recorder may already
    // be observing the anonymous page. Stop that observer as well.
    posthog.stopSessionRecording();
    suppressedInternal = true;
    return;
  }

  posthog.identify(userId, { email, name });

  // A signed-in human outranks the scanner latch from here on: an email-link
  // scanner detonates a URL, it does not complete a Clerk sign-in. Placed after
  // the internal opt-out above (which returns) so an internal account can never
  // un-silence a session, and after the real identify so it means what it says.
  markSessionIdentified();

  // Fire the deferred pageview now that we know this is a real user.
  posthog.capture('$pageview');
}

/**
 * Track an event. No-ops when PostHog is not initialized or user is suppressed.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY || !initialized || suppressedInternal) return;

  try {
    if (event === NETWORK_FAILURE_EVENT && properties) {
      if (!posthog.has_opted_out_capturing()) networkFailureReporter(properties as NetworkFailureProps);
      return;
    }
    posthog.capture(event, properties);
  } catch (err) {
    console.error('[analytics] PostHog capture failed:', err);
  }
}

/**
 * Fire the `extension_install_landed` event when the extension handed off its
 * anonymous id on the URL (`?from=install&oqa_ext_anon=anon_...`). Uses a raw
 * capture under that same anon id so the install and landing events share one
 * identity and fold into the user when the extension later identifies them —
 * WITHOUT mutating posthog-js's own distinct_id (the dashboard runs
 * `person_profiles: 'identified_only'`). Call once at app entry. Fire-and-forget.
 */
export function captureInstallLanded(): void {
  if (isExtensionSessionRoute(window.location.pathname)) return;
  if (!POSTHOG_KEY) return;

  const landing = resolveInstallLanding(
    window.location.search,
    window.location.hostname,
    window.navigator,
  );
  if (!landing) return;

  void fetch(`${POSTHOG_HOST}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: POSTHOG_KEY,
      distinct_id: landing.extAnonId,
      event: 'extension_install_landed',
      properties: { returning_device: landing.returning },
    }),
  }).catch(() => {
    // Analytics must never break the page.
  });
}
